// Phase 2 — Programmatic Board × Subject × City SEO pages.
// URL pattern: /{board}-{subject}-tutor-{city}
// e.g. /ib-math-tutor-mumbai, /igcse-physics-tutor-delhi, /cbse-chemistry-tutor-bangalore
//
// 6 boards × 12 subjects × 20 cities = 1,440 programmatic pages, all rendered
// via BoardSubjectCity.jsx from these tables. Every page emits its own SEO
// meta + Course + Service + FAQ JSON-LD.
//
// Restricted-term rule: The phrase "Home Tuition" is reserved for Jabalpur,
// Bhopal, Nagpur only. All 20 programmatic cities below are strictly framed as
// "1:1 Online Tutor" (or online-friendly variations). Legacy "home-tuition-"
// slugs in EXTENDED_CITIES are retained ONLY for URL-level SEO authority.

export const PROG_BOARDS = {
 "cbse": { label: "CBSE", full: "CBSE (Central Board of Secondary Education)" },
 "icse": { label: "ICSE", full: "ICSE / ISC (Council for the Indian School Certificate Examinations)" },
 "ib": { label: "IB", full: "IB (International Baccalaureate) MYP & DP" },
 "igcse": { label: "IGCSE", full: "IGCSE (Cambridge International Examinations)" },
 "a-level":{ label: "A-Level",full: "AS / A-Level (Cambridge, Edexcel, AQA, OCR)" },
 "state-board": { label: "State Board", full: "Indian State Boards (MP, MH, UP, Karnataka, TN, WB, GJ)" },
};

export const PROG_SUBJECTS = {
 "math": { label: "Math", full: "Mathematics", aliases: ["math", "maths", "mathematics"] },
 "physics": { label: "Physics", full: "Physics", aliases: ["physics"] },
 "chemistry": { label: "Chemistry", full: "Chemistry", aliases: ["chemistry"] },
 "biology": { label: "Biology", full: "Biology", aliases: ["biology", "bio"] },
 "english": { label: "English", full: "English Language & Literature", aliases: ["english"] },
 "hindi": { label: "Hindi", full: "Hindi (Elective + Second Language)", aliases: ["hindi"] },
 "economics": { label: "Economics", full: "Economics", aliases: ["economics", "econ"] },
 "accountancy":{ label: "Accountancy",full: "Accountancy", aliases: ["accountancy", "accounts"] },
 "business-studies": { label: "Business Studies", full: "Business Studies", aliases: ["business studies"] },
 "computer-science": { label: "Computer Science", full: "Computer Science (with Python / Java / C++)", aliases: ["cs", "computer science"] },
 "social-studies": { label: "Social Studies", full: "Social Studies (History, Geography, Civics)", aliases: ["social studies", "sst"] },
 "sanskrit": { label: "Sanskrit", full: "Sanskrit", aliases: ["sanskrit"] },
};

// 20 priority cities — reused from EXTENDED_CITIES / IndiaHubs.
// citySlug -> {name, state}. Full offline cities (Jabalpur, Bhopal, Nagpur)
// are intentionally excluded here — their programmatic pages already exist
// under the existing CityTopicLanding infrastructure.
export const PROG_CITIES = {
 "mumbai": { name: "Mumbai", state: "Maharashtra" },
 "delhi": { name: "Delhi NCR", state: "Delhi NCR" },
 "bangalore": { name: "Bangalore", state: "Karnataka" },
 "pune": { name: "Pune", state: "Maharashtra" },
 "hyderabad": { name: "Hyderabad", state: "Telangana" },
 "chennai": { name: "Chennai", state: "Tamil Nadu" },
 "kolkata": { name: "Kolkata", state: "West Bengal" },
 "ahmedabad": { name: "Ahmedabad", state: "Gujarat" },
 "indore": { name: "Indore", state: "Madhya Pradesh" },
 "lucknow": { name: "Lucknow", state: "Uttar Pradesh" },
 "jaipur": { name: "Jaipur", state: "Rajasthan" },
 "kanpur": { name: "Kanpur", state: "Uttar Pradesh" },
 "surat": { name: "Surat", state: "Gujarat" },
 "vadodara": { name: "Vadodara", state: "Gujarat" },
 "coimbatore":{ name: "Coimbatore",state: "Tamil Nadu" },
 "kochi": { name: "Kochi", state: "Kerala" },
 "chandigarh":{ name: "Chandigarh",state: "Chandigarh" },
 "gurgaon": { name: "Gurgaon", state: "Haryana" },
 "noida": { name: "Noida", state: "Uttar Pradesh" },
 "patna": { name: "Patna", state: "Bihar" },
};

export function getAllProgSlugs() {
 const slugs = [];
 for (const b of Object.keys(PROG_BOARDS)) {
 for (const s of Object.keys(PROG_SUBJECTS)) {
 for (const c of Object.keys(PROG_CITIES)) {
 slugs.push(`${b}-${s}-tutor-${c}`);
 }
 }
 }
 return slugs;
}

// Data resolver used by BoardSubjectCity.jsx
export function getBoardSubjectCityData(boardSlug, subjectSlug, citySlug) {
 const board = PROG_BOARDS[boardSlug];
 const subject = PROG_SUBJECTS[subjectSlug];
 const city = PROG_CITIES[citySlug];
 if (!board || !subject || !city) return null;

 const path = `/${boardSlug}-${subjectSlug}-tutor-${citySlug}`;
 const canonical = `https://anandtutorials.com${path}`;

 const h1 = `${board.label} ${subject.label} Tutor in ${city.name} — 1:1 Live Online Classes`;
 const seoTitle = `${board.label} ${subject.label} Tutor in ${city.name} | Best 1:1 Online ${subject.label} Tuition | Anand Tutorials`;
 const seoDescription = `Best 1:1 live online ${board.label} ${subject.label} tutor in ${city.name}. Verified Cambridge / IIT / AIIMS educated mentors. Free demo class. All grades, ${board.label} pattern, ${subject.label} specialists since 2013. Call our Customer Care team.`;
 const keywords = [
 `${board.label} ${subject.label} tutor ${city.name}`,
 `${board.label} ${subject.label} tuition ${city.name}`,
 `online ${subject.label} tutor ${city.name}`,
 `${board.label} tutor ${city.name}`,
 `${subject.label} online classes ${city.name}`,
 `best ${board.label} ${subject.label} tutor in ${city.name}`,
 `Anand Tutorials ${city.name}`,
 `1:1 online ${subject.label} tuition ${city.name}`,
 `${board.label} ${subject.label} online coaching ${city.name}`,
 ].join(", ");

 return {
 path,
 canonical,
 board,
 subject,
 city,
 boardSlug,
 subjectSlug,
 citySlug,
 h1,
 seoTitle,
 seoDescription,
 keywords,
 intro: `Looking for a top-rated ${board.label} ${subject.label} tutor in ${city.name}? Anand Tutorials matches your child with a verified ${board.full} ${subject.full} specialist — delivered live 1:1 online over Zoom or Google Meet, with sessions recorded for revision and weekly Sunday parent reports. Our ${city.name} online tutors are Cambridge, IIT and AIIMS alumni, with 5+ years of ${board.label} board and mark-scheme experience.`,
 eyebrow: `${board.label} · ${subject.label} · ${city.name}`,
 whyLocal: [
 `Cambridge / IIT / AIIMS educated ${subject.label} specialists with 5+ years of ${board.label} teaching experience.`,
 `Live 1:1 two-way HD video on Zoom or Google Meet — one tutor, one student, one personalised plan.`,
 `${city.name} school pacing aware — chapter-by-chapter aligned to your child's actual school calendar.`,
 `${board.label}-specific mark-scheme training, exam-blueprint drills, previous-year question paper walkthroughs.`,
 `Every session recorded for revision. Weekly Sunday parent reports.`,
 `Free tutor replacement if you're not satisfied within the first 2 sessions.`,
 ],
 faqs: [
 [`Do you provide ${board.label} ${subject.label} tuition in ${city.name}?`,
 `Yes — 1:1 live online ${board.label} ${subject.label} tuition for students anywhere in ${city.name}. Our tutors join live on Zoom or Google Meet at your child's preferred slot. Just an internet connection is needed.`],
 [`Are your ${board.label} ${subject.label} tutors qualified?`,
 `Yes — every ${city.name} ${board.label} ${subject.label} tutor is hand-picked from Cambridge, IIT, AIIMS, IIT Bombay / Delhi / Kanpur / Madras and Ivy League alumni pools. Every tutor is interviewed, demo-tested and background-verified. Less than 12% of applicants make it into our network.`],
 [`How much does ${board.label} ${subject.label} tuition in ${city.name} cost?`,
 `Fees depend on grade, session frequency and tutor seniority. We share a transparent monthly quote at the free demo — no hidden charges, no lock-in contracts, cancel anytime.`],
 [`Is the demo class free?`,
 `Yes — 100% free, full-length demo class with the matched ${board.label} ${subject.label} tutor. No advance payment required. Parents may attend the entire demo.`],
 [`Do you cover ${board.label} board / competitive exam preparation?`,
 `Yes — full ${board.label} board preparation with mark-scheme aligned answer-writing, PYQ walkthroughs and weekly mock tests. Competitive exam prep (${board.label === "IB" || board.label === "IGCSE" || board.label === "A-Level" ? "SAT, ACT, IB / IGCSE Predicted Grades" : "JEE, NEET, NTSE, KVPY, Olympiads"}) is also available 1:1.`],
 [`Can I get a female ${board.label} ${subject.label} tutor in ${city.name}?`,
 `Yes — verified female ${board.label} ${subject.label} tutors are available for ${city.name} students. Request at the time of booking, no extra charge.`],
 [`Does Anand Tutorials also send home tutors to ${city.name}?`,
 `Anand Tutorials in-home tutor visits are available strictly in Jabalpur, Bhopal and Nagpur only. For ${city.name}, our elite 1:1 live online tutor network delivers the same personal attention with zero commute.`],
 ],
 };
}
